import { ChangeEvent, useMemo } from 'react';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SimpleSelect } from '@/components/ui/simple-select';

import { AccessType } from '@/types/property.types';
import { accessTypeOptionsForOrderSummary } from '../../../../config/options.config';
import { formatPhoneNumberInField } from '../../../../util/formatting';
import type { FormErrors } from '../hooks/useAccessFormValidation';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AccessFormValues {
  accessType: string | null;
  lockboxLocation: string | null;
  accessCode: string | null;
  rentlyRegistrationPin: string | null;
  contactName: string | null;
  contactNumber: string | null;
  additionalNotes: string | undefined;
}

interface PropertyAccessFormProps {
  values: AccessFormValues;
  onChange: (field: keyof AccessFormValues, value: string | null) => void;
  errors: FormErrors;
  isCartOrder: boolean;
  defaultNotes?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function showLockboxLocation(type: string | null): boolean {
  return type === AccessType.KEYPAD || type === AccessType.LOCKBOX;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PropertyAccessForm = ({
  values,
  onChange,
  errors,
  isCartOrder,
  defaultNotes
}: PropertyAccessFormProps) => {
  const widthClass = isCartOrder ? 'w-full' : 'w-6/12';

  const selectedAccessTypeOption = useMemo(
    () =>
      accessTypeOptionsForOrderSummary.find(
        (o) => o.value === values.accessType
      ) ?? null,
    [values.accessType]
  );

  const handleAccessTypeChange = (_field: string, value: string) => {
    onChange('accessType', value);
  };

  return (
    <div className="space-y-4">
      {/* Access type select */}
      <div className={widthClass}>
        <SimpleSelect
          options={accessTypeOptionsForOrderSummary}
          name="accessType"
          label="Property Access Type (e.g. lockbox, keypad, etc)"
          controlId="accessType"
          isRequired
          placeholder="Select"
          ariaDescribedBy="accessType-select"
          maxMenuHeight={240}
          errors={errors.accessType}
          value={selectedAccessTypeOption}
          setFieldValue={handleAccessTypeChange}
          field="accessType"
        />
      </div>

      {/* Lockbox location (only for Lockbox) */}
      {values.accessType === AccessType.LOCKBOX && (
        <div className={widthClass}>
          <FieldGroup
            id="lockbox-location"
            label="Lockbox Location"
            required
            error={errors.lockboxLocation}
          >
            <Input
              id="lockbox-location"
              type="text"
              maxLength={255}
              placeholder="Enter Lockbox Location"
              defaultValue={values.lockboxLocation ?? ''}
              data-testid="lockbox-location_id"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChange('lockboxLocation', e.target.value || null)
              }
            />
          </FieldGroup>
        </div>
      )}

      {/* Access code (for Lockbox or Keypad) */}
      {showLockboxLocation(values.accessType) && (
        <div className={widthClass}>
          <FieldGroup
            id="access-code"
            label="Access Code"
            required
            error={errors.accessCode}
            hint="Between 3 and 15 characters"
          >
            <Input
              id="access-code"
              type="text"
              maxLength={15}
              minLength={3}
              placeholder="Enter Access Code"
              defaultValue={values.accessCode ?? ''}
              data-testid="access-code_id"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChange('accessCode', e.target.value || null)
              }
            />
          </FieldGroup>
        </div>
      )}

      {/* Rently registration pin */}
      {values.accessType === AccessType.RENTLY && (
        <div className={widthClass}>
          <FieldGroup
            id="rentlyRegistrationPin"
            label="Rently Registration PIN"
            required
            error={errors.rentlyRegistrationPin}
          >
            <Input
              id="rentlyRegistrationPin"
              type="text"
              maxLength={15}
              minLength={3}
              placeholder="Enter Rently Registration PIN"
              defaultValue={values.rentlyRegistrationPin ?? ''}
              data-testid="rentlyRegistrationPin_id"
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChange(
                  'rentlyRegistrationPin',
                  e.target.value || null
                )
              }
            />
          </FieldGroup>
        </div>
      )}

      {/* Contact name */}
      <div className={widthClass}>
        <FieldGroup
          id="contact-person-name"
          label="Contact Name (for Any On-Site Issues)"
          required
          error={errors.contactName}
        >
          <Input
            id="contact-person-name"
            type="text"
            maxLength={255}
            placeholder="Enter Name"
            defaultValue={values.contactName ?? ''}
            data-testid="contact-person-name_id"
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              onChange('contactName', e.target.value || null)
            }
          />
        </FieldGroup>
      </div>

      {/* Contact phone number */}
      <div className={widthClass}>
        <FieldGroup
          id="contact-person-number"
          label="Contact Phone Number"
          required
          error={errors.contactNumber}
        >
          <Input
            id="contact-person-number"
            type="text"
            placeholder="Enter Number"
            value={formatPhoneNumberInField(values.contactNumber || '')}
            data-testid="contact-person-number_id"
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const formatted = formatPhoneNumberInField(e.target.value);
              onChange('contactNumber', formatted || null);
            }}
          />
        </FieldGroup>
      </div>

      {/* Additional notes */}
      <div className={widthClass}>
        <div className="space-y-1.5">
          <Label htmlFor="additionalNotes">
            Additional Notes for the Photographer
          </Label>
          <textarea
            id="additionalNotes"
            rows={5}
            maxLength={1000}
            placeholder="Enter Notes"
            defaultValue={defaultNotes ?? ''}
            data-testid="additionalNotes_id"
            className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
              onChange('additionalNotes', e.target.value)
            }
          />
        </div>
      </div>
    </div>
  );
};

export default PropertyAccessForm;

// ---------------------------------------------------------------------------
// Internal helper: form field wrapper with label + error
// ---------------------------------------------------------------------------

function FieldGroup({
  id,
  label,
  required,
  error,
  hint,
  children
}: {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        {required && (
          <span className="text-destructive ml-0.5">*</span>
        )}
      </Label>
      {children}
      {error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
      {hint && !error && (
        <p className="text-xs text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}
